import React from 'react';
import { useLocation } from 'react-router-dom';
import { AlertCircle, RefreshCw } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { isChunkLoadError, reloadOnceForChunkError } from '@/lib/chunkErrors';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, chunk: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error, chunk: isChunkLoadError(error) };
  }

  // Leaving the broken page clears the crash, so one page that failed does
  // not take the rest of the portal down with it (7 Oct 2026).
  componentDidUpdate(prev) {
    if (this.state.hasError && prev.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false, error: null, errorInfo: null, chunk: false });
    }
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error("ErrorBoundary caught an error:", error, errorInfo);
    // Record the crash where a probe or the admin desk can read it — until
    // 28 Sep 2026 the only copy of a render crash was the user's screen,
    // and diagnosis stalled on a screenshot. Fire-and-forget: reporting
    // must never be able to make the crash screen itself crash.
    try {
      supabase.from('client_errors').insert({
        path: String(window.location?.pathname || '').slice(0, 500),
        error_text: String(error || '').slice(0, 4000),
        component_stack: String(errorInfo?.componentStack || '').slice(0, 8000),
        user_agent: String(navigator?.userAgent || '').slice(0, 500),
      }).then(({ error: e }) => {
        if (e) console.error('crash report not stored:', e.message);
      });
    } catch { /* never let reporting throw */ }

    // A page's code that did not download: a fresh document fetches it again.
    // Once per two minutes at most (chunkErrors.js), after the report is sent.
    if (isChunkLoadError(error)) {
      setTimeout(() => reloadOnceForChunkError(this.props.reloadOptions), 400);
    }
  }

  render() {
    if (this.state.hasError && this.state.chunk) {
      return (
        <div role="alert" className="mx-auto my-16 max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
          <RefreshCw className="mx-auto mb-3 h-8 w-8 text-slate-400" aria-hidden="true" />
          <h2 className="text-lg font-bold text-slate-900">This page did not load</h2>
          <p className="mt-2 text-sm text-slate-600">
            Part of the portal could not be downloaded. This usually means the connection dropped for a moment, or the portal was just updated.
          </p>
          <button type="button" onClick={() => window.location.reload()}
            className="mt-4 inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-700">
            <RefreshCw className="h-4 w-4" aria-hidden="true" /> Reload the portal
          </button>
          <p className="mt-3 text-xs text-slate-500">Still stuck after reloading? Open portal.parcelink.org in another browser or on another network.</p>
        </div>
      );
    }
    if (this.state.hasError) {
      return (
        <div className="p-8 m-8 border-2 border-red-500 bg-red-50 rounded-lg overflow-auto max-h-[80vh]">
          <div className="flex items-center gap-3 text-red-600 font-bold mb-4 text-xl">
            <AlertCircle size={32} />
            <h2>Something went wrong in the component tree.</h2>
          </div>
          <p className="text-red-800 font-mono bg-red-100 p-4 rounded mb-4">
            {this.state.error && this.state.error.toString()}
          </p>
          <pre className="text-sm text-slate-800 bg-slate-100 p-4 rounded whitespace-pre-wrap">
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}

/** The app-level boundary, reset whenever the page changes. */
export function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

export default ErrorBoundary;
